import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import ICategory from "../models/ICategory";
import IOnDeleteAttributeDTO from "../models/DTO/IOnDeleteAttributeDTO";

/**
 * Options for fetching categories
 */
export interface IGetCategoriesOptions {
    catalogGuid?: string;
    onlyRootCategories?: boolean;
    isVendorCategory?: boolean;
    isOrderCategory?: boolean;
    isTakeOffCategory?: boolean;
    isHomeCategories?: boolean;
    noIncludes?: boolean;
}

interface CategoriesApiResponse {
    categories: ICategory[];
}

/**
 * Fetch categories via REST API
 */
const getCategories = async (
    options: IGetCategoriesOptions,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<ICategory[]> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.CategoriesGet,
        query: options,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
    }

    const data: CategoriesApiResponse = await response.json();
    return data.categories;
};

/**
 * Delete an attribute from a category via REST API. The API also clears the attribute's value
 * from the category's items. Throws with the server's message (e.g. system attributes) on failure.
 */
const deleteCategoryAttribute = async (
    categoryGuid: string,
    attributeGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IOnDeleteAttributeDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CategoryDeleteAttribute,
        params: { categoryGuid, attributeGuid },
    });

    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "DELETE",
        headers,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete attribute: ${response.statusText}`);
    }

    return (await response.json()) as IOnDeleteAttributeDTO;
};

const CategoryService = {
    getCategories,
    deleteCategoryAttribute,
};

export default CategoryService;
